// Single source of truth for /tools/compare-loan-estimates.

const SITE = "https://www.homeapproach.com";

export const PAGE_PATH = "/tools/compare-loan-estimates";
export const PAGE_URL = `${SITE}${PAGE_PATH}`;
export const PAGE_TITLE =
  "How to Compare Loan Estimates Line by Line (Annotated Guide + Worksheet)";
export const PAGE_DESCRIPTION =
  "Have two or three Loan Estimates in hand? Compare them line by line: the apples-to-apples check, Page 1 costs, Page 2 Sections A–J, Page 3 APR and TIP, plus a side-by-side worksheet and a lender negotiation script.";

export const HERO_PROMISE =
  "The Loan Estimate is a standardized three-page form, which means every lender's numbers sit in the same place. Once you know where to look, comparing offers is mechanical: confirm the loans are actually the same, read the same three boxes on each form, and see who wins on total cost — not on the headline rate.";

export const HERO_NEXT_STEPS = [
  "Run the apples-to-apples check first. If the loan amount, term, purpose, or lock status differ, you are not comparing offers — you are comparing scenarios.",
  "Fill in the worksheet below as you read. Seven rows decide almost every comparison.",
];

export const SECTIONS = [
  { id: "apples", title: "The apples-to-apples check" },
  { id: "page-1", title: "Compare Page 1" },
  { id: "page-2", title: "Compare Page 2 (Sections A–J)" },
  { id: "page-3", title: "Compare Page 3 (Comparisons box)" },
  { id: "examples", title: "Annotated examples" },
  { id: "worksheet", title: "Side-by-side worksheet" },
  { id: "mistakes", title: "Why one estimate looks cheaper" },
  { id: "checklist", title: "Winner checklist" },
  { id: "faq", title: "Common questions" },
  { id: "negotiate", title: "Negotiation script" },
];

export const COMPLIANCE_NOTE =
  "Loan Estimate examples on this page are illustrative and use round numbers, not quotes. Fee names, escrow estimates, and section totals vary by lender, loan type, state, and property. Always compare identical loan scenarios — same loan amount, term, purpose, and lock status — before deciding.";

export const DISCLAIMER =
  "Educational information only, not lending, legal, or tax advice. Home Approach is not a lender or mortgage broker. Confirm every figure with the lenders who issued your Loan Estimates.";

/* ---------------- Apples-to-apples gate ---------------- */

export interface GateItem {
  id: string;
  label: string;
  body: string;
}

export const APPLES_TO_APPLES: GateItem[] = [
  {
    id: "amount",
    label: "Same loan amount",
    body: "A $5,000 difference in loan amount changes the payment, the closing costs, and the APR. If one lender assumed a different down payment, ask for a revised estimate at the same amount before you compare anything.",
  },
  {
    id: "term",
    label: "Same loan term and product",
    body: "A 30-year fixed and a 7/6 ARM are not comparable, and neither is a 30-year against a 25-year. Check the top-left of Page 1: loan term, purpose, product, and loan type (Conventional, FHA, VA, USDA) must match across every estimate.",
  },
  {
    id: "purpose",
    label: "Same loan purpose and property",
    body: "Purchase versus refinance, primary residence versus investment, and single-family versus condo all move pricing. The property address and estimated value at the top of Page 1 should be identical.",
  },
  {
    id: "lock",
    label: "Same rate lock status",
    body: "Page 1 states whether the rate is locked and until when. A floating rate is not a promise. An unlocked estimate will often look cheaper than a locked one simply because it was priced on a different day or without lock cost built in.",
  },
  {
    id: "date",
    label: "Estimates from the same week",
    body: "Rates move daily. Comparing a Monday estimate against one from three weeks ago tells you about the market, not about the lenders. Ask for refreshed estimates dated within a few days of each other.",
  },
];

/* ---------------- Page-by-page walkthrough ---------------- */

export interface LineItem {
  id: string;
  term: string;
  where: string;
  meaning: string;
  watch: string;
}

export const PAGE_1_ITEMS: LineItem[] = [
  {
    id: "rate",
    term: "Interest rate",
    where: "Loan Terms box, top of Page 1",
    meaning: "The rate used to calculate your principal and interest payment. It is not the full cost of the loan.",
    watch: "Right next to it the form answers whether the rate can increase after closing. A low rate with a 'YES' is an adjustable loan.",
  },
  {
    id: "pi",
    term: "Monthly principal & interest",
    where: "Loan Terms box",
    meaning: "The portion of the payment that goes to the loan itself.",
    watch: "This is the number lenders quote in ads. It excludes taxes, insurance, and mortgage insurance, so it always understates your real payment.",
  },
  {
    id: "penalty",
    term: "Prepayment penalty / balloon payment",
    where: "Loan Terms box, bottom rows",
    meaning: "Whether you can be charged for paying the loan off early, or owe a lump sum at the end.",
    watch: "Both should read NO on a standard purchase loan. A YES on either is a reason to eliminate the offer, not to negotiate it.",
  },
  {
    id: "projected",
    term: "Projected payments",
    where: "Middle of Page 1",
    meaning: "Principal and interest, mortgage insurance, and an estimate of taxes, insurance, and assessments — the closest thing to your real monthly cost.",
    watch: "Check whether escrow is included. One lender escrowing taxes and another not escrowing will show wildly different monthly totals for the same loan.",
  },
  {
    id: "mi-drop",
    term: "Mortgage insurance duration",
    where: "Projected payments table, later year columns",
    meaning: "Whether mortgage insurance falls off and when.",
    watch: "FHA mortgage insurance usually runs the life of the loan; conventional PMI typically drops. That difference can be worth tens of thousands.",
  },
  {
    id: "cash",
    term: "Estimated cash to close",
    where: "Bottom-right of Page 1",
    meaning: "Down payment plus closing costs, minus credits and deposits already paid.",
    watch: "This is a headline number driven by Page 2. Never compare cash to close without also comparing the rate that produced it.",
  },
];

export const PAGE_2_ITEMS: LineItem[] = [
  {
    id: "a",
    term: "Section A — Origination charges",
    where: "Top-left of Page 2",
    meaning: "What the lender charges to make the loan: points, application fee, underwriting fee, processing fee.",
    watch: "This is the most negotiable block on the entire form, and the one most worth comparing head to head. Points appear here as a percentage of the loan amount.",
  },
  {
    id: "b",
    term: "Section B — Services you cannot shop for",
    where: "Left column, Page 2",
    meaning: "Appraisal, credit report, flood determination, and similar lender-chosen services.",
    watch: "Amounts should be broadly similar across lenders. A large outlier here is worth a question.",
  },
  {
    id: "c",
    term: "Section C — Services you can shop for",
    where: "Left column, Page 2",
    meaning: "Title search, lender's title insurance, settlement or closing fee, survey.",
    watch: "You are allowed to use your own provider. A lender with high Section C is not necessarily expensive — it may just be quoting its preferred title company.",
  },
  {
    id: "e",
    term: "Section E — Taxes and other government fees",
    where: "Right column, Page 2",
    meaning: "Recording fees and transfer taxes.",
    watch: "These are set by your state and county, so they should match closely. A gap usually means one lender estimated sloppily.",
  },
  {
    id: "f",
    term: "Section F — Prepaids",
    where: "Right column, Page 2",
    meaning: "Prepaid interest, the first year of homeowner's insurance, and prepaid property taxes.",
    watch: "Prepaid interest depends on your assumed closing date. A lender assuming a month-end closing will show far less prepaid interest — that is timing, not savings.",
  },
  {
    id: "g",
    term: "Section G — Initial escrow payment",
    where: "Right column, Page 2",
    meaning: "The cushion deposited into your escrow account at closing.",
    watch: "Escrow deposits are money you keep, held on your behalf. Do not treat a bigger escrow deposit as a worse deal.",
  },
  {
    id: "j",
    term: "Section J — Total closing costs and lender credits",
    where: "Bottom of Page 2",
    meaning: "Sections A–I added up, then lender credits subtracted to give the net figure.",
    watch: "A large negative lender credit almost always means a higher interest rate. Compare Section J only alongside the rate and APR.",
  },
];

export const PAGE_3_ITEMS: LineItem[] = [
  {
    id: "five-year",
    term: "In 5 years — total you will have paid",
    where: "Comparisons box, top of Page 3",
    meaning: "Principal, interest, mortgage insurance, and loan costs paid through year five.",
    watch: "The single best apples-to-apples number on the form for buyers who will not keep the loan 30 years.",
  },
  {
    id: "principal",
    term: "In 5 years — principal paid",
    where: "Comparisons box",
    meaning: "How much of that money reduced your balance rather than went to the lender.",
    watch: "Two loans with the same five-year total can differ here. More principal paid means more equity for the same spend.",
  },
  {
    id: "apr",
    term: "APR",
    where: "Comparisons box",
    meaning: "The rate that expresses interest plus most loan costs as a single annual percentage.",
    watch: "APR assumes you keep the loan to term. It rewards low fees and punishes points, which can mislead you if you plan to move or refinance in five years.",
  },
  {
    id: "tip",
    term: "Total Interest Percentage (TIP)",
    where: "Comparisons box",
    meaning: "Total interest paid over the full loan term as a percentage of the loan amount.",
    watch: "Useful for long-horizon comparisons; irrelevant if you will not hold the loan to maturity.",
  },
  {
    id: "servicing",
    term: "Other considerations — servicing",
    where: "Lower Page 3",
    meaning: "Whether the lender intends to service your loan or sell it.",
    watch: "Not a cost, but it decides who you will actually be dealing with for the next 30 years.",
  },
];

/* ---------------- Annotated examples ---------------- */

export interface AnnotatedPanel {
  id: string;
  page: string;
  title: string;
  rows: { label: string; value: string; callout?: number }[];
  callouts: { n: number; text: string }[];
}

export const ANNOTATED_PANELS: AnnotatedPanel[] = [
  {
    id: "panel-1",
    page: "Page 1",
    title: "Loan Terms and Estimated Cash to Close",
    rows: [
      { label: "Loan amount", value: "$320,000" },
      { label: "Interest rate", value: "6.500%", callout: 1 },
      { label: "Monthly principal & interest", value: "$2,023" },
      { label: "Prepayment penalty", value: "NO" },
      { label: "Estimated total monthly payment", value: "$2,684", callout: 2 },
      { label: "Estimated cash to close", value: "$91,340", callout: 3 },
    ],
    callouts: [
      { n: 1, text: "Confirm the lock box directly beneath the rate. An unlocked 6.500% is not the same offer as a locked 6.500%." },
      { n: 2, text: "This total includes the estimated taxes and insurance line. Check whether escrow is included on both estimates before comparing." },
      { n: 3, text: "Cash to close is the output of Page 2. Read it last, not first." },
    ],
  },
  {
    id: "panel-2",
    page: "Page 2",
    title: "Closing Cost Details — Sections A through J",
    rows: [
      { label: "A. Origination charges", value: "$3,850", callout: 1 },
      { label: "  0.5% of loan amount (points)", value: "$1,600" },
      { label: "B. Services you cannot shop for", value: "$695" },
      { label: "C. Services you can shop for", value: "$2,140", callout: 2 },
      { label: "E. Taxes and government fees", value: "$1,205" },
      { label: "F. Prepaids", value: "$2,860" },
      { label: "G. Initial escrow payment", value: "$1,410" },
      { label: "J. Total closing costs", value: "$12,160" },
      { label: "  Lender credits", value: "-$1,500", callout: 3 },
    ],
    callouts: [
      { n: 1, text: "Section A is where lenders differ most and where negotiation actually works. Compare it dollar for dollar." },
      { n: 2, text: "Section C is shoppable. A high number here may be a pricey title company, not a pricey lender." },
      { n: 3, text: "A credit this size usually means a slightly higher rate. Trace it back to the rate on Page 1 before you celebrate." },
    ],
  },
  {
    id: "panel-3",
    page: "Page 3",
    title: "Comparisons box",
    rows: [
      { label: "In 5 years — total paid", value: "$130,480", callout: 1 },
      { label: "In 5 years — principal paid", value: "$21,940", callout: 2 },
      { label: "Annual Percentage Rate (APR)", value: "6.812%", callout: 3 },
      { label: "Total Interest Percentage (TIP)", value: "112.4%" },
    ],
    callouts: [
      { n: 1, text: "The cleanest single comparison for most buyers: same loan, same horizon, one number." },
      { n: 2, text: "Higher principal paid for the same total means you are buying equity, not just paying interest." },
      { n: 3, text: "APR folds fees into the rate. It only tells the truth if you keep the loan to term." },
    ],
  },
];

/* ---------------- Worksheet ---------------- */

export interface WorksheetRow {
  id: string;
  label: string;
  hint: string;
  placeholder: string;
  lowerIsBetter?: boolean;
}

export const WORKSHEET_ROWS: WorksheetRow[] = [
  { id: "lock", label: "Rate locked? (Y/N + date)", hint: "Page 1, next to the interest rate", placeholder: "Y — 9/14" },
  { id: "rate", label: "Interest rate", hint: "Page 1, Loan Terms", placeholder: "6.500%", lowerIsBetter: true },
  { id: "points", label: "Points paid", hint: "Page 2, Section A", placeholder: "$1,600", lowerIsBetter: true },
  { id: "sectionA", label: "Section A total", hint: "Page 2, origination charges", placeholder: "$3,850", lowerIsBetter: true },
  { id: "net", label: "Net closing costs (J minus credits)", hint: "Page 2, bottom", placeholder: "$10,660", lowerIsBetter: true },
  { id: "apr", label: "APR", hint: "Page 3, Comparisons", placeholder: "6.812%", lowerIsBetter: true },
  { id: "tip", label: "TIP", hint: "Page 3, Comparisons", placeholder: "112.4%", lowerIsBetter: true },
  { id: "principal5", label: "Principal paid in 5 years", hint: "Page 3, Comparisons", placeholder: "$21,940" },
];

export const LENDER_COLUMNS = ["Lender A", "Lender B", "Lender C"] as const;

/* ---------------- Mistakes ---------------- */

export interface Mistake {
  id: string;
  title: string;
  body: string;
}

export const MISTAKES: Mistake[] = [
  {
    id: "lock",
    title: "Mismatched lock status",
    body: "An estimate priced on a floating rate can undercut a locked competitor by an eighth of a point or more, and that advantage disappears the day you lock. Ask both lenders what the rate would be locked for the same number of days, then compare.",
  },
  {
    id: "prepaids",
    title: "Prepaid taxes and insurance estimates",
    body: "Sections F and G are estimates the lender chose. A lender assuming a cheap insurance policy or a month-end closing shows a smaller cash to close without offering you anything better. These costs are yours either way — strip them out and compare Sections A through C instead.",
  },
  {
    id: "credits",
    title: "Lender credits masking a higher rate",
    body: "A $4,000 credit that buys you a quarter-point higher rate can cost far more than $4,000 over the time you keep the loan. Whenever you see a large credit, check the five-year total on Page 3 against the competing offer.",
  },
  {
    id: "points",
    title: "Points lowering the rate while raising upfront cash",
    body: "Points are prepaid interest. They make the rate and the APR look better and the cash to close look worse. Divide the point cost by the monthly savings to get a break-even in months; if you are likely to move or refinance before then, the points lose.",
  },
  {
    id: "escrow",
    title: "One estimate with escrow, one without",
    body: "A non-escrowed loan shows a lower monthly payment and lower cash to close, but you still owe the taxes and insurance. Compare principal, interest, and mortgage insurance only, then add the same tax and insurance figure to both.",
  },
];

/* ---------------- Winner checklist ---------------- */

export interface ChecklistItem {
  id: string;
  text: string;
}

export const WINNER_CHECKLIST: ChecklistItem[] = [
  { id: "same-loan", text: "All estimates show the same loan amount, term, product, purpose, and property." },
  { id: "same-week", text: "All estimates are dated within a few days of each other." },
  { id: "lock-known", text: "I know the lock status and lock length on every offer." },
  { id: "section-a", text: "I compared Section A origination charges dollar for dollar." },
  { id: "credits-traced", text: "Every lender credit is traced back to the rate that produced it." },
  { id: "shoppable", text: "I checked whether Section C services can be shopped for a lower price." },
  { id: "five-year", text: "I compared the five-year total and principal paid on Page 3." },
  { id: "horizon", text: "I picked APR or the five-year total based on how long I actually plan to keep the loan." },
  { id: "no-penalty", text: "No estimate has a prepayment penalty or balloon payment." },
  { id: "negotiated", text: "I asked the runner-up to match the winner's Section A, credit, or rate." },
];

/* ---------------- FAQ ---------------- */

export interface Faq {
  id: string;
  q: string;
  a: string;
}

export const FAQS: Faq[] = [
  {
    id: "cfpb-method",
    q: "What is the standard way to compare Loan Estimates?",
    a: "Because the Loan Estimate is a standardized federal form, the comparison is positional: line up the forms, confirm the loan scenario matches, then compare the same boxes — the interest rate and estimated total monthly payment on Page 1, Section A and the net Section J figure on Page 2, and the five-year totals plus APR and TIP on Page 3. The CFPB publishes a blank annotated form you can hold next to your own.",
  },
  {
    id: "apr-vs-rate",
    q: "Is a lower APR always better than a lower interest rate?",
    a: "No. APR bundles most loan costs into a single annual rate assuming you keep the loan for its full term. If you expect to sell or refinance within five to seven years, the five-year total-paid figure on Page 3 is a better decision number, because a loan with heavy upfront points can win on APR while costing you more in the years you actually hold it.",
  },
  {
    id: "points-worth",
    q: "When are points worth paying?",
    a: "Divide the cost of the points by the monthly payment savings they buy. That is your break-even in months. If you will comfortably stay past the break-even — and you would not rather have that cash for reserves, repairs, or a larger down payment — the points can pay off. If you are unsure how long you will keep the loan, they usually do not.",
  },
  {
    id: "escrow-differ",
    q: "Why do escrow and prepaid estimates differ so much between lenders?",
    a: "Sections F and G are lender estimates, not quotes. They depend on the assumed closing date, the insurance premium the lender plugged in, and how many months of taxes the lender chose to collect up front. Those numbers move your cash to close without changing the cost of the loan, so they should not decide the winner.",
  },
  {
    id: "lender-credits",
    q: "How should lender credits affect my decision?",
    a: "Treat a credit as a discount you are financing. Ask the lender what the rate would be with no credit, then compare the credited and non-credited versions on the Page 3 five-year total. Credits are genuinely useful when cash at closing is your binding constraint — just price what that convenience costs over time.",
  },
  {
    id: "how-many",
    q: "How many Loan Estimates should I get?",
    a: "Three is the practical sweet spot: enough to see the spread in Section A and rate pricing, few enough to actually read carefully. Requesting them within roughly a two-week window means the credit inquiries are generally treated as a single event for mortgage shopping.",
  },
];

/* ---------------- Negotiation script ---------------- */

export interface Script {
  id: string;
  title: string;
  text: string;
}

export const NEGOTIATION_SCRIPTS: Script[] = [
  {
    id: "section-a",
    title: "Ask a lender to match Section A fees",
    text: "I have a competing Loan Estimate for the same loan amount, term, and lock period. Their Section A origination charges total $____ versus your $____. Can you match or beat that Section A total without changing my interest rate? If you can, please send a revised Loan Estimate.",
  },
  {
    id: "credit",
    title: "Ask for a lender credit",
    text: "Your rate is competitive but my cash to close is tight. At my current rate, what lender credit can you offer, and what rate would that credit require? Please show me both versions as Loan Estimates so I can compare the five-year totals.",
  },
  {
    id: "rate",
    title: "Ask a lender to match the rate",
    text: "I have a locked estimate at ____% with net closing costs of $____ on the same loan. I would rather work with you. Can you match that rate at comparable costs? If not, what is the best combination of rate and credit you can lock today?",
  },
  {
    id: "reprice",
    title: "Ask for a re-price after a market move",
    text: "Rates have moved since my estimate was issued on ____. Can you re-price my scenario today and send an updated Loan Estimate so I can compare it against my other offers on the same date?",
  },
];

/* ---------------- JSON-LD ---------------- */

export const LOAN_ESTIMATE_COMPARE_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "HowTo",
      name: "How to compare Loan Estimates line by line",
      description: PAGE_DESCRIPTION,
      step: [
        { "@type": "HowToStep", position: 1, name: "Run the apples-to-apples check", text: "Confirm every estimate shows the same loan amount, term, product, purpose, property, and rate lock status.", url: `${PAGE_URL}#apples` },
        { "@type": "HowToStep", position: 2, name: "Compare Page 1", text: "Compare the interest rate, projected payments, and estimated cash to close.", url: `${PAGE_URL}#page-1` },
        { "@type": "HowToStep", position: 3, name: "Compare Page 2", text: "Compare Sections A through J, especially origination charges and lender credits.", url: `${PAGE_URL}#page-2` },
        { "@type": "HowToStep", position: 4, name: "Compare Page 3", text: "Compare APR, TIP, five-year total paid, and principal paid in five years.", url: `${PAGE_URL}#page-3` },
        { "@type": "HowToStep", position: 5, name: "Fill the worksheet and negotiate", text: "Record each lender's seven decisive figures, pick the winner, then ask the runner-up to match.", url: `${PAGE_URL}#worksheet` },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
